// « Activer les rappels » : la permission part d'un geste, puis une notification
// de test part tout de suite et l'on confirme l'avoir reçue (parcours, étape 3).
// « Je l'ai reçue » attend aussi l'accusé du service worker côté serveur (#25).
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { AideContact, Bouton, Erreur, SousTitre, Texte, Titre } from '../components/base';
import { attendreAccuse } from '../lib/accuse';
import { api } from '../lib/api';
import { noterTestRecu } from '../lib/jeton';
import { activerRappels, type EtatPush, etatPush, plateforme } from '../lib/push';
import { CLES } from '../lib/requetes';

function AideNonRecue() {
  const { t } = useTranslation();
  const aide = t(`activer.aide.${plateforme()}`, { returnObjects: true }) as string[];
  return (
    <>
      <SousTitre>{t('activer.aideTitre')}</SousTitre>
      <ul className="text-texte-long mt-3 list-disc space-y-2 pl-5 leading-relaxed">
        {aide.map((a) => (
          <li key={a}>{a}</li>
        ))}
      </ul>
      <AideContact texte={t('activer.aideContact')} />
    </>
  );
}

/** Notifications refusées : le chemin pour les rouvrir, un geste par ligne (#14). */
function Bloquees() {
  const { t } = useTranslation();
  const etapes = t(`activer.refuseEtapes.${plateforme()}`, { returnObjects: true }) as string[];
  return (
    <div role="alert" className="mt-4">
      <p className="text-alerte font-medium">{t('activer.refuse')}</p>
      <ol className="text-texte-long mt-3 list-decimal space-y-2 pl-5 leading-relaxed">
        {etapes.map((e) => (
          <li key={e}>{e}</li>
        ))}
      </ol>
    </div>
  );
}

/** La notification est vue, mais son accusé n'est pas arrivé au serveur (#25). */
function AccuseAbsent({ onContinuer }: Readonly<{ onContinuer: () => void }>) {
  const { t } = useTranslation();
  const etapes = t('accuse.absentEtapes', { returnObjects: true }) as string[];
  return (
    <>
      <SousTitre>{t('accuse.absentTitre')}</SousTitre>
      <Texte>{t('accuse.absent')}</Texte>
      <ol className="text-texte-long mt-3 list-decimal space-y-2 pl-5 leading-relaxed">
        {etapes.map((e) => (
          <li key={e}>{e}</li>
        ))}
      </ol>
      <Bouton variante="secondaire" className="mt-6" onClick={onContinuer}>
        {t('accuse.continuer')}
      </Bouton>
    </>
  );
}

function Test({
  role,
  envoiIds,
  onRenvoi,
  onRecu,
}: Readonly<{
  role: 'elle' | 'proche';
  envoiIds: string[];
  onRenvoi: (ids: string[]) => void;
  onRecu: () => void;
}>) {
  const { t } = useTranslation();
  const [nonRecue, setNonRecue] = useState(false);
  const verifier = useMutation({
    mutationFn: () => attendreAccuse(envoiIds),
    onSuccess: (accuse) => {
      if (accuse) onRecu();
    },
  });
  return (
    <section>
      <Titre>{t('activer.testTitre')}</Titre>
      <Texte>{t(`activer.testTexte.${role}`)}</Texte>
      <div className="mt-6 flex flex-wrap gap-3">
        <Bouton disabled={verifier.isPending} onClick={() => verifier.mutate()}>
          {verifier.isPending ? t('accuse.verification') : t('activer.recue')}
        </Bouton>
        <Bouton variante="secondaire" onClick={() => setNonRecue(true)}>
          {t('activer.pasRecue')}
        </Bouton>
      </div>
      {verifier.data === false && <AccuseAbsent onContinuer={onRecu} />}
      {(nonRecue || verifier.data === false) && (
        <>
          {nonRecue && <AideNonRecue />}
          <Renvoyer
            onRenvoi={(ids) => {
              verifier.reset();
              onRenvoi(ids);
            }}
          />
        </>
      )}
    </section>
  );
}

/** Renvoie une notification de test ; les nouveaux envois sont ceux à vérifier. */
function Renvoyer({ onRenvoi }: Readonly<{ onRenvoi: (ids: string[]) => void }>) {
  const { t } = useTranslation();
  const renvoyer = useMutation({
    mutationFn: api.tester,
    onSuccess: (r) => onRenvoi(r.envois.map((e) => e.envoiId)),
  });
  return (
    <Bouton
      variante="secondaire"
      className="mt-6"
      disabled={renvoyer.isPending}
      onClick={() => renvoyer.mutate()}
    >
      {t('activer.renvoyer')}
    </Bouton>
  );
}

interface Props {
  role: 'elle' | 'proche';
  clePublique: string;
  /** Le service de notification a désabonné le dernier appareil (#41). */
  perdu?: boolean;
  onTermine: () => void;
}

export function ActiverRappels({ role, clePublique, perdu = false, onTermine }: Readonly<Props>) {
  const { t } = useTranslation();
  const client = useQueryClient();
  const [etat, setEtat] = useState<EtatPush>(etatPush);
  const [envoiIds, setEnvoiIds] = useState<string[]>([]);
  const activer = useMutation({
    mutationFn: async () => {
      const r = await activerRappels(clePublique);
      setEtat(r);
      if (r === 'actif') setEnvoiIds((await api.tester()).envois.map((e) => e.envoiId));
      return r;
    },
  });

  if (activer.data === 'actif') {
    return (
      <Test
        role={role}
        envoiIds={envoiIds}
        onRenvoi={setEnvoiIds}
        onRecu={() => {
          noterTestRecu();
          void client.invalidateQueries({ queryKey: CLES.moi });
          onTermine();
        }}
      />
    );
  }
  return (
    <section>
      <Titre>{t('activer.titre')}</Titre>
      {perdu && (
        <p role="alert" className="text-alerte mt-4 font-medium">
          {t(`activer.perdu.${role}`)}
        </p>
      )}
      <Texte>{t(`activer.pourquoi.${role}`)}</Texte>
      {etat === 'refuse' && <Bloquees />}
      {etat === 'non-pris-en-charge' && <Erreur>{t('activer.nonPrisEnCharge')}</Erreur>}
      <Bouton
        className="mt-6"
        disabled={activer.isPending || etat === 'non-pris-en-charge'}
        onClick={() => activer.mutate()}
      >
        {t('activer.bouton')}
      </Bouton>
      {activer.isError && <Erreur>{t('erreur.action')}</Erreur>}
    </section>
  );
}
